import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import clsx from "classnames";
import type { FormState } from "final-form";
import type { ComponentProps, FC, PropsWithChildren } from "react";
import type React from "react";
import { useCallback } from "react";
import { Form } from "react-bootstrap";
import { Form as FinalForm, FormSpy } from "react-final-form";
import { useEventTracking } from "../../../bm/utils/useEventTracking";
import { useSmallCalculator } from "./SmallCalculatorContext";

interface Props<Values> {
	id?: string;
	className?: string;
	initialValues?: Partial<Values>;
	disabled?: boolean;
	onSubmit?: (values: Values) => Promise<void>;
	onChange?: (values: Values) => void;
}

const SmallCalculatorStep: React.FC<PropsWithChildren<Props<unknown>>> = <FormFields,>({
	id,
	className,
	initialValues = {},
	children,
	onSubmit = () => Promise.resolve(),
	onChange,
}: PropsWithChildren<Props<FormFields>>) => {
	const { sendEvent } = useEventTracking();
	const { activeStep, classNames, onStep } = useSmallCalculator();

	const handleFormSubmit = useCallback(
		async (values: FormFields) => {
			sendEvent("BM_stepValid", {
				buttonId: GoogleAnalytics4.generateId("bi"),
				stepName: id,
				...values,
			});

			try {
				await onSubmit(values);
				await onStep(id, values);
			} catch (error) {
				sendEvent("BM_stepError", {
					buttonId: GoogleAnalytics4.generateId("bi"),
					stepName: id,
					error: error.message,
				});
			}
		},
		[id, onStep, onSubmit, sendEvent],
	);

	if (id !== activeStep) {
		return null;
	}

	return (
		<FinalForm<FormFields> initialValues={initialValues} onSubmit={handleFormSubmit}>
			{({ handleSubmit, errors, hasValidationErrors }) => (
				<Form
					as="form"
					className={clsx(className, classNames?.card, "card", "small-calculator-step")}
					onSubmit={(element) => {
						if (hasValidationErrors) {
							sendEvent("BM_stepInvalid", {
								buttonId: GoogleAnalytics4.generateId("bi"),
								stepName: id,
								//stepNumber,
								...errors,
							});
						}

						return handleSubmit(element);
					}}
					noValidate
				>
					<FormSpy
						subscription={{ touched: true }}
						onChange={(props: FormState<FormFields>) => {
							if (Object.values(props.touched).includes(true)) {
								onChange?.(props.values);
							}
						}}
					/>
					{children}
				</Form>
			)}
		</FinalForm>
	);
};

export const createStep = <Values extends Record<string, any>, Comp extends FC<any> = FC<any>>(
	Component: Comp,
): FC<Props<Values> & ComponentProps<Comp>> => {
	return ({ id, initialValues = {}, onSubmit = () => Promise.resolve(), onChange, ...props }) => {
		return (
			<SmallCalculatorStep id={id} initialValues={initialValues} onSubmit={onSubmit} onChange={onChange}>
				<Component {...(props as ComponentProps<Comp>)} />
			</SmallCalculatorStep>
		);
	};
};
